/**
 * E06-F05-T05 — typed api-client + Query hooks for the manual AI-check screen.
 *
 * Feature-owned (NOT in the shared queries.ts / queryKeys.ts). Uses the shared
 * `apiClient` (injects the session Bearer token, normalizes the response, never
 * throws) + `unwrap` (the single throw-at-the-Query-boundary helper). Every payload
 * is validated against the shared contract (`research-pipeline.ts` /
 * `research-ai-chain.ts`) at the boundary, so a drifted shape fails loudly.
 *
 * Endpoints (routes/research-pipeline.ts, mounted under /research):
 *   GET  /research/:requestId/companies                   → rows + per-row aiStatus
 *   POST /research/:requestId/companies/:rowId/run-ai     → start ONE paid chain
 *   POST /research/:requestId/companies/:rowId/rerun-ai   → retry a failed row
 *   POST /research/:requestId/companies/run-ai            → start the selected set
 *   GET  /research/:requestId/companies/run-ai/quote      → FREE: verdict path + price per company
 *   GET  /research/:requestId/companies/:rowId/sources    → collected pages + text
 *   POST /research/:requestId/companies/:rowId/manual-check → FREE: save a manual check
 *
 * NO automatic paid work anywhere in this module: the three POSTs are exposed ONLY
 * as mutations (never as a query, never in an effect), so a paid chain can start only
 * from a user click in the view (no-automatic-AI, decision-log 2026-07-20). The list
 * query and the sources query are free reads — the list self-polls while any row is
 * mid-flight and stops on its own once every run is terminal.
 */
import {
  useQuery,
  useMutation,
  useQueryClient,
  type UseQueryResult,
  type UseMutationResult,
} from '@tanstack/react-query';
import {
  pipelineListResponseSchema,
  aiRunOneResponseSchema,
  aiRunBatchResponseSchema,
  aiCheckQuoteResponseSchema,
  collectedSourcesResponseSchema,
  manualCheckResponseSchema,
  type ManualCheckInput,
  type ManualCheckResponse,
  type PipelineListResponse,
  type AiRunOneResponse,
  type AiRunBatchResponse,
  type AiCheckQuoteResponse,
  type CollectedSourcesResponse,
} from '@eer/shared';
import { apiClient, unwrap, type ApiResultError } from '../api/client.js';
import { hasActiveRuns } from './researchAiChainModel.js';

// ---------------------------------------------------------------------------
// query keys (feature-local)
// ---------------------------------------------------------------------------

export const aiChainKeys = {
  all: ['research', 'aichain'] as const,
  rows: (requestId: number) => ['research', 'aichain', 'rows', requestId] as const,
  quote: (requestId: number) => ['research', 'aichain', 'quote', requestId] as const,
  sources: (requestId: number, rowId: number) =>
    ['research', 'aichain', 'sources', requestId, rowId] as const,
} as const;

/** Status-poll cadence while at least one company's check is mid-flight. */
export const AI_CHAIN_POLL_INTERVAL_MS = 3000 as const;

// ---------------------------------------------------------------------------
// fetchers / mutators
// ---------------------------------------------------------------------------

/** GET the long-list rows with their per-company AI-run status (free read). */
export async function fetchAiChainRows(requestId: number): Promise<PipelineListResponse> {
  const result = await apiClient.get<unknown>(`/research/${requestId}/companies`, {
    command: 'research_pipeline_list',
  });
  return pipelineListResponseSchema.parse(unwrap(result));
}

/** POST run-ai for ONE company — PAID. Only ever called from a click handler. */
export async function runAiOne(requestId: number, rowId: number): Promise<AiRunOneResponse> {
  const result = await apiClient.post<unknown>(
    `/research/${requestId}/companies/${rowId}/run-ai`,
    undefined,
    { command: 'research_run_ai' },
  );
  return aiRunOneResponseSchema.parse(unwrap(result));
}

/** POST rerun-ai — the per-row Retry button on a failed check (also re-scrapes). */
export async function rerunAiOne(requestId: number, rowId: number): Promise<AiRunOneResponse> {
  const result = await apiClient.post<unknown>(
    `/research/${requestId}/companies/${rowId}/rerun-ai`,
    undefined,
    { command: 'research_rerun_ai' },
  );
  return aiRunOneResponseSchema.parse(unwrap(result));
}

/** POST run-ai for the checkbox-selected set — PAID, confirmed by the view first. */
export async function runAiBatch(
  requestId: number,
  rowIds: readonly number[],
): Promise<AiRunBatchResponse> {
  const result = await apiClient.post<unknown>(
    `/research/${requestId}/companies/run-ai`,
    { rowIds: [...rowIds] },
    { command: 'research_run_ai_batch' },
  );
  return aiRunBatchResponseSchema.parse(unwrap(result));
}

/**
 * E12-F17-T06 — GET the verdict path in force + the estimated price of ONE company's chain
 * (free read: no AI call, no write) for the batch confirmation.
 */
export async function fetchAiCheckQuote(requestId: number): Promise<AiCheckQuoteResponse> {
  const result = await apiClient.get<unknown>(`/research/${requestId}/companies/run-ai/quote`, {
    command: 'research_ai_check_quote',
  });
  return aiCheckQuoteResponseSchema.parse(unwrap(result));
}

/** GET the collected pages + pre-model text for one company (free read). */
export async function fetchCollectedSources(
  requestId: number,
  rowId: number,
): Promise<CollectedSourcesResponse> {
  const result = await apiClient.get<unknown>(`/research/${requestId}/companies/${rowId}/sources`, {
    command: 'research_ai_sources',
  });
  return collectedSourcesResponseSchema.parse(unwrap(result));
}

/**
 * E06-F05-T16 — POST the manual check of a company whose site blocked the AI check.
 * FREE (no AI call): the server scores the verdicts with the same weighted math.
 */
export async function submitManualCheck(
  requestId: number,
  rowId: number,
  body: ManualCheckInput,
): Promise<ManualCheckResponse> {
  const result = await apiClient.post<unknown>(
    `/research/${requestId}/companies/${rowId}/manual-check`,
    body,
    { command: 'research_manual_check' },
  );
  return manualCheckResponseSchema.parse(unwrap(result));
}

// ---------------------------------------------------------------------------
// hooks
// ---------------------------------------------------------------------------

/**
 * The rows + their AI status. Polls ONLY while some row is mid-flight (gathering /
 * extracting / verifying) and stops by itself once every run reached a terminal
 * state — the paid work is manual and pointwise, so there is nothing to watch when
 * the list is idle.
 */
export function useAiChainRows(
  requestId: number,
  enabled: boolean = true,
): UseQueryResult<PipelineListResponse, ApiResultError> {
  return useQuery<PipelineListResponse, ApiResultError>({
    queryKey: aiChainKeys.rows(requestId),
    queryFn: () => fetchAiChainRows(requestId),
    enabled: enabled && requestId > 0,
    refetchInterval: (query) => {
      const rows = query.state.data?.rows ?? [];
      return hasActiveRuns(rows) ? AI_CHAIN_POLL_INTERVAL_MS : false;
    },
  });
}

/** Start ONE company's paid check (bound to a row button). */
export function useRunAiOne(
  requestId: number,
): UseMutationResult<AiRunOneResponse, ApiResultError, number> {
  const queryClient = useQueryClient();
  return useMutation<AiRunOneResponse, ApiResultError, number>({
    mutationFn: (rowId) => runAiOne(requestId, rowId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: aiChainKeys.rows(requestId) });
    },
  });
}

/** Retry a FAILED company's check (bound to that row's Retry button). */
export function useRerunAiOne(
  requestId: number,
): UseMutationResult<AiRunOneResponse, ApiResultError, number> {
  const queryClient = useQueryClient();
  return useMutation<AiRunOneResponse, ApiResultError, number>({
    mutationFn: (rowId) => rerunAiOne(requestId, rowId),
    onSuccess: (_data, rowId) => {
      void queryClient.invalidateQueries({ queryKey: aiChainKeys.rows(requestId) });
      // A re-run re-scrapes, so the previously collected pages are about to change.
      void queryClient.invalidateQueries({ queryKey: aiChainKeys.sources(requestId, rowId) });
      // E07-F04-T12 — the Review table's "Refresh" of an earlier-collected company uses
      // this same call; its row flips to "Refreshing…" (key literal = reviewKeys.list).
      void queryClient.invalidateQueries({ queryKey: ['research', 'review', requestId] });
    },
  });
}

/** Start the selected set (bound to the bulk button, AFTER the confirmation). */
export function useRunAiBatch(
  requestId: number,
): UseMutationResult<AiRunBatchResponse, ApiResultError, readonly number[]> {
  const queryClient = useQueryClient();
  return useMutation<AiRunBatchResponse, ApiResultError, readonly number[]>({
    mutationFn: (rowIds) => runAiBatch(requestId, rowIds),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: aiChainKeys.rows(requestId) });
    },
  });
}

/**
 * The collected-sources panel payload for ONE row. Disabled while the panel is
 * closed (`rowId === null`) so opening the screen never fires it.
 */
export function useCollectedSources(
  requestId: number,
  rowId: number | null,
): UseQueryResult<CollectedSourcesResponse, ApiResultError> {
  return useQuery<CollectedSourcesResponse, ApiResultError>({
    queryKey: aiChainKeys.sources(requestId, rowId ?? 0),
    queryFn: () => fetchCollectedSources(requestId, rowId as number),
    enabled: requestId > 0 && rowId != null,
  });
}

/**
 * E12-F17-T06 — the AI-check quote, fetched only while the confirmation is open (`enabled`) and
 * refetched on every open, so the path toggle and the price are current. Free read.
 */
export function useAiCheckQuote(
  requestId: number,
  enabled: boolean,
): UseQueryResult<AiCheckQuoteResponse, ApiResultError> {
  return useQuery<AiCheckQuoteResponse, ApiResultError>({
    queryKey: aiChainKeys.quote(requestId),
    queryFn: () => fetchAiCheckQuote(requestId),
    enabled: enabled && requestId > 0,
    staleTime: 0,
    refetchOnMount: 'always',
  });
}

/** E06-F05-T16 — save a manual check (bound to the manual-check drawer's Save). */
export function useSubmitManualCheck(
  requestId: number,
): UseMutationResult<
  ManualCheckResponse,
  ApiResultError,
  { rowId: number; body: ManualCheckInput }
> {
  const queryClient = useQueryClient();
  return useMutation<
    ManualCheckResponse,
    ApiResultError,
    { rowId: number; body: ManualCheckInput }
  >({
    mutationFn: ({ rowId, body }) => submitManualCheck(requestId, rowId, body),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: aiChainKeys.rows(requestId) });
      // The Review table reads the same row (key literal = reviewKeys.list).
      void queryClient.invalidateQueries({ queryKey: ['research', 'review', requestId] });
    },
  });
}
